import { Howl, Howler } from 'howler';
import type {
  AudioLoadCatalogOptions,
  AudioPlayOptions,
  AudioService,
} from './AudioService';

const DEFAULT_POOL = 4;
const SINGLE_INSTANCE_POOL = 1;

type HowlMap = Map<string, Howl>;

function clamp01(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.min(1, Math.max(0, value));
}

/**
 * Howler-backed audio service: unlock, SFX play with pool, mute, visibility pause.
 * No React, no screen knowledge — only string ids → URLs.
 */
export function createAudioService(): AudioService {
  if (process.env.NODE_ENV === 'test') {
    return createNoOpAudioService();
  }

  const howls: HowlMap = new Map();
  let muted = false;
  let masterVolume = 1;
  let unlocked = false;
  let hiddenByVisibility = false;

  const applyMute = () => {
    Howler.mute(muted || hiddenByVisibility);
  };

  const onVisibilityChange = () => {
    hiddenByVisibility = document.visibilityState === 'hidden';
    applyMute();
  };

  const onPageHide = () => {
    hiddenByVisibility = true;
    applyMute();
  };

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);
  }

  Howler.volume(masterVolume);

  const playId = (id: string, options?: AudioPlayOptions) => {
    const bypassMuteGate = options?.bypassMuteGate === true;
    if (!bypassMuteGate && (muted || hiddenByVisibility)) {
      return;
    }
    const howl = howls.get(id);
    if (howl == null) {
      return;
    }
    howl.volume(clamp01(options?.volume ?? 1));
    howl.play();
  };

  return {
    unlock() {
      if (unlocked) {
        return;
      }
      unlocked = true;
      try {
        const ctx = Howler.ctx;
        if (ctx != null && typeof ctx.resume === 'function') {
          void ctx.resume();
        }
      } catch {
        // ignore — unlock is best-effort
      }
    },

    loadCatalog(urls, options?: AudioLoadCatalogOptions) {
      const singleInstanceIds = options?.singleInstanceIds;
      const loopIds = options?.loopIds;
      for (const [id, src] of Object.entries(urls)) {
        if (src === '' || howls.has(id)) {
          continue;
        }
        const pool =
          singleInstanceIds?.has(id) === true
            ? SINGLE_INSTANCE_POOL
            : DEFAULT_POOL;
        howls.set(
          id,
          new Howl({
            src: [src],
            preload: true,
            pool,
            volume: 1,
            loop: loopIds?.has(id) === true,
          }),
        );
      }
    },

    play(id, options?: AudioPlayOptions) {
      playId(id, options);
    },

    stop(id) {
      howls.get(id)?.stop();
    },

    toggle(id, options?: AudioPlayOptions) {
      const howl = howls.get(id);
      if (howl == null) {
        return;
      }
      if (howl.playing()) {
        howl.stop();
        return;
      }
      playId(id, options);
    },

    isPlaying(id) {
      return howls.get(id)?.playing() === true;
    },

    onEnd(id, callback) {
      howls.get(id)?.on('end', callback);
    },

    setMuted(nextMuted: boolean) {
      muted = nextMuted;
      applyMute();
    },

    isMuted() {
      return muted;
    },

    setMasterVolume(volume: number) {
      masterVolume = clamp01(volume);
      Howler.volume(masterVolume);
    },

    dispose() {
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('pagehide', onPageHide);
      }
      for (const howl of howls.values()) {
        howl.unload();
      }
      howls.clear();
    },
  };
}

function createNoOpAudioService(): AudioService {
  let muted = false;
  return {
    unlock() {},
    loadCatalog() {},
    play() {},
    stop() {},
    toggle() {},
    isPlaying() {
      return false;
    },
    onEnd() {},
    setMuted(next) {
      muted = next;
    },
    isMuted() {
      return muted;
    },
    setMasterVolume(_volume) {
      // no-op in tests
    },
    dispose() {},
  };
}
